import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Input, CardActions } from 'react-toolbox';
import { ThemeProvider } from 'react-css-themr';
import FlexBox from 'flexbox-react';
import ReactTooltip from 'react-tooltip';
import FontIcon from 'react-toolbox/lib/font_icon';
import cx from 'classnames';
import {
  fetchHolidayPromoSetting,
  updateHolidayPromoSetting,
} from 'actionCreators/holiday_promo_settings';
import {
  getHolidayPromoSetting,
  getHolidayPromoSettingLoading,
  getHolidayPromoSettingErrors,
} from '../../../../reducers';
import { error } from '../../../../utils';
import Card from 'components/Card';
import ColoredButton from 'components/ColoredButton';
import Loading from 'components/Loading';
import PageActionBar from 'components/PageActionBar';
import DateInput from 'components/DatePicker';
import ReactSelect from 'components/StyledSelect/react_select';
import styles from 'styles/form';
import RTInput from 'styles/input';
import contentAreaStyles from 'styles/contentArea';
import ownStyles from './styles.scss';

const theme = { RTInput };
const fieldWidth = cx({ [styles.inputs]: true, [styles.largeInput]: true });
const cancelClass = cx([styles.cancelButton, styles.actionButton]);

const HOLIDAY_PROMO_VENDORS = ['LHI', 'IFI'];

const availableVendorsFor = (user) => {
  const mvw = (user && user.manage_vendor_workrooms) || {};
  return HOLIDAY_PROMO_VENDORS.filter(vendor => mvw[vendor.toLowerCase()] === true);
};

const YEAR_FIELD = {
  key: 'promo_year',
  label: 'Promo Year',
  tooltip: "Which year's Thanksgiving & Christmas the promo targets; holiday dates are derived from this year.",
};

const DATE_FIELDS = [
  {
    key: 'promo_start_date',
    label: 'Holiday Promo Start',
    tooltip: 'Earliest sold date that qualifies, and the date the promo goes live.',
  },
  {
    key: 'thanksgiving_sold_end_date',
    label: 'Thanksgiving Sales Cutoff Date',
    tooltip: 'Latest sold date eligible for the Thanksgiving promo.',
  },
  {
    key: 'thanksgiving_cutoff_date',
    label: 'Thanksgiving Scheduling Cutoff Date',
    tooltip: 'After this date, a phase can no longer be flagged for Thanksgiving; it rolls over to Christmas.',
  },
  {
    key: 'christmas_sold_end_date',
    label: 'Christmas Sales Cutoff Date',
    tooltip: 'Latest sold date eligible for the Christmas promo.',
  },
  {
    key: 'promo_end_date',
    label: 'Holiday Promo End Date',
    tooltip: 'The entire promo ends on this date.',
  },
];

const FIELD_KEYS = [YEAR_FIELD.key, ...DATE_FIELDS.map(f => f.key)];

const formFromSetting = (setting) => {
  if (!setting) return {};
  return FIELD_KEYS.reduce((acc, key) => {
    acc[key] = setting[key] == null ? '' : String(setting[key]);
    return acc;
  }, {});
};

class HolidayPromoSettings extends Component {
  constructor(props) {
    super(props);
    const [vendor] = availableVendorsFor(props.user);
    this.state = { form: formFromSetting(props.setting), vendor };
  }

  componentWillMount() {
    if (this.state.vendor) this.props.fetchHolidayPromoSetting(this.state.vendor);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.setting && nextProps.setting !== this.props.setting) {
      this.setState({ form: formFromSetting(nextProps.setting) });
    }
  }

  handleYearChange = value =>
    this.setState({ form: { ...this.state.form, [YEAR_FIELD.key]: value } });

  handleDateChange = key => value =>
    this.setState({ form: { ...this.state.form, [key]: value } });

  handleVendorChange = (selectedOption) => {
    const vendor = selectedOption && selectedOption.value;
    if (!vendor || vendor === this.state.vendor) return;
    this.setState({ vendor, form: {} });
    this.props.fetchHolidayPromoSetting(vendor);
  }

  handleCancel = () => this.setState({ form: formFromSetting(this.props.setting) });

  handleSave = () => {
    const { form, vendor } = this.state;
    const attrs = { ...form };
    DATE_FIELDS.forEach(({ key }) => {
      const rawValue = attrs[key];
      attrs[key] = rawValue
        ? moment(rawValue, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
        : '';
    });
    this.props.updateHolidayPromoSetting(attrs, vendor).catch(() => {});
  }

  renderFieldLabel = ({ key, label, tooltip }) => (
    <span className={ownStyles.labelContent}>
      {label}
      <FontIcon
        value="info_outline"
        className={ownStyles.infoIcon}
        data-tip
        data-for={`hps-${key}`}
      />
      <ReactTooltip id={`hps-${key}`} place="top" effect="solid">
        <span className={ownStyles.tooltipText}>{tooltip}</span>
      </ReactTooltip>
    </span>
  )

  renderDateField = (field) => {
    const { errors } = this.props;
    return (
      <DateInput
        key={field.key}
        className={fieldWidth}
        label={this.renderFieldLabel(field)}
        value={this.state.form[field.key] || ''}
        onChange={this.handleDateChange(field.key)}
        error={error(field.key, errors)}
      />
    );
  }

  render() {
    const { loading, errors, user } = this.props;
    const { vendor } = this.state;
    const availableVendors = availableVendorsFor(user);

    if (!vendor) {
      return (
        <div>
          <PageActionBar showAdd={false} showFiltersBtn={false} title="Holiday Promo Settings" />
          <div className={contentAreaStyles.contentAreaContainer}>
            <p>You don&apos;t have access to any vendor&apos;s Holiday Promo Settings.</p>
          </div>
        </div>
      );
    }

    const title = availableVendors.length > 1 ? 'Holiday Promo Settings' : `Holiday Promo Settings - ${vendor}`;

    return (
      <div>
        <PageActionBar
          showAdd={false}
          showFiltersBtn={false}
          title={title}
        />
        <div className={contentAreaStyles.contentAreaContainer}>
          {loading && <Loading />}
          {!loading && (
            <ThemeProvider theme={theme}>
              <Card>
                <div className={styles.divider}>
                  <h5 className={styles.cardTitle}>Promo Window</h5>
                  <p className={styles.cardSubtitle}>
                    Configure when the &quot;Install by the Holidays&quot; promo is active and
                    which sold dates and appointments qualify.
                  </p>
                </div>
                {availableVendors.length > 1 && (
                  <FlexBox flexWrap="wrap" className={styles.divider}>
                    <ReactSelect
                      key="vendor"
                      className={fieldWidth}
                      label="Vendor"
                      clearable={false}
                      options={availableVendors.map(v => ({ value: v, label: v }))}
                      value={{ value: vendor, label: vendor }}
                      onChange={this.handleVendorChange}
                    />
                  </FlexBox>
                )}
                <FlexBox flexWrap="wrap" className={styles.divider}>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    label={this.renderFieldLabel(YEAR_FIELD)}
                    className={fieldWidth}
                    value={this.state.form[YEAR_FIELD.key] || ''}
                    onChange={this.handleYearChange}
                    error={error(YEAR_FIELD.key, errors)}
                  />
                  {DATE_FIELDS.map(this.renderDateField)}
                </FlexBox>
                <FlexBox justifyContent="flex-end" flexWrap="wrap">
                  <CardActions className={styles.actionsWrapper}>
                    <ColoredButton
                      className={cancelClass}
                      responsive
                      color="gray"
                      label="Cancel"
                      onClick={this.handleCancel}
                    />
                    <ColoredButton
                      className={styles.actionButton}
                      responsive
                      color="green"
                      label="Save Changes"
                      onClick={this.handleSave}
                    />
                  </CardActions>
                </FlexBox>
              </Card>
            </ThemeProvider>
          )}
        </div>
      </div>
    );
  }
}

HolidayPromoSettings.defaultProps = {
  setting: null,
  loading: false,
  errors: {},
  user: {},
};

HolidayPromoSettings.propTypes = {
  setting: PropTypes.object,
  loading: PropTypes.bool,
  errors: PropTypes.object,
  user: PropTypes.object,
  fetchHolidayPromoSetting: PropTypes.func.isRequired,
  updateHolidayPromoSetting: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  setting: getHolidayPromoSetting(state),
  loading: getHolidayPromoSettingLoading(state),
  errors: getHolidayPromoSettingErrors(state),
  user: state.user,
});

const mapDispatchToProps = {
  fetchHolidayPromoSetting,
  updateHolidayPromoSetting,
};

export default connect(mapStateToProps, mapDispatchToProps)(HolidayPromoSettings);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/holidayPromoSettings/index.jsx